import React, { Component } from 'react';
import { isEqual, omit } from 'lodash';
import PropTypes from 'prop-types';
import ExportableTable from 'containers/tables/ExportableTable';
import helpers from 'helpers';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { insertBeforeItem } from 'helpers/utilities';
import { generateFooterData, generateHeaders } from 'helpers/tables';
import { reducePCVData } from 'helpers/social';

const DEFAULT_ACCESSORS = ['impressions', 'clicks', 'cpc', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversion_rate', 'engagements', 'engagement_rate', 'purchase_roas', 'budget'];

class SocialAdSetOverviewPivotTable extends Component {
    constructor(props) {
        super(props);
        const {
            data,
            columns,
            uniqueCampaigns
        } = SocialAdSetOverviewPivotTable.createTableData(this.props.adSets, props);

        this.state = {
            data,
            columns,
            uniqueCampaigns,
            title: props.title,
            exportName: props.exportName,
            adSetParams: (this.props.adSets || {}).params
        };

        this.thProps = this.thProps.bind(this);
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps.adSets, this.props.adSets);
    }

    thProps(state, rowInfo, column) {
        return {
            style: { outline: 0, textTransform: 'uppercase' },
            ...(column.tooltip && { title: column.tooltip })
        };
    }

    static createTableData(adSetApiResponse = {}, props) {
        let data = [];
        let uniqueCampaigns = 10;
        let keys = [
            'impressions', 'clicks', 'link_clicks', 'conversions', 'conversion_rate',
            'engagements', 'engagement_rate', 'cpc', 'ctr', 'cpconv',
            'cost_per_link_click', 'link_click_rate', 'leads', 'leadgen_grouped', 'cost_per_lead', 'cost_per_leadgen_grouped', 'purchase_roas', 'purchase_conversion_value', 'budget'
        ];

        if (adSetApiResponse.hasData) {
            const { adSetIdMap } = adSetApiResponse;
            data = Object.entries(adSetIdMap).map(c => ({
                adSetId: c[0],
                adSetName: c[1].name,
                ...omit(c[1], ['name']),
                ...adSetApiResponse[c[0]].statistics
            }));

            uniqueCampaigns = new Set(data.map(c => c.campaignName)).size;
        }

        const { hasROASData, sumPCVWithoutZeroes, sumSpendWithoutZeroes } = reducePCVData({ data });

        let accessors = DEFAULT_ACCESSORS;
        if (!hasROASData) {
            accessors = DEFAULT_ACCESSORS.filter(key => key !== 'purchase_roas');
            keys = keys.filter(key => key !== 'purchase_roas');
        }

        if (props.showConversionRevenue) {
            accessors = insertBeforeItem(accessors, 'budget', 'purchase_conversion_value');
        }

        const headers = generateHeaders({
            keys,
            footerData: generateFooterData({
                data,
                filteredKeys: [
                    'campaignId', 'campaignName', 'adSetName', 'adSetId', 'objective'
                ],
                extraData: { sumPCVWithoutZeroes, sumSpendWithoutZeroes }
            }),
            initial: {
                campaignName: {
                    Header: 'Campaign',
                    accessor: 'campaignName',
                    minWidth: 200,
                    Cell: row => <CellDiv value={row.value} />,
                    PivotValue: ({ value }) => <span>{value}</span>,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                adSetName: {
                    Header: 'Ad Set',
                    accessor: 'adSetName',
                    minWidth: 200,
                    Cell: row => <CellDiv value={row.value} style={{ textAlign: 'right' }} />,
                    Aggregated: (row) => {
                        const count = row.subRows.length;
                        let value = `${count} Ad Sets`;
                        if (count === 1) {
                            value = row.subRows[0].adSetName;
                        } else if (row.isExpanded) {
                            value = '';
                        }
                        return (
                            <CellDiv
                                strong={count !== 1}
                                value={value}
                                style={{ textAlign: 'right' }}
                            />
                        );
                    },
                    Footer: (
                        <CellDiv value=' ' strong />
                    )
                }
            }
        });

        if (props.showSemCpconv && !accessors.includes('cpconv')) {
            accessors = insertBeforeItem(accessors, 'engagements', 'cpconv');
        }

        const columns = [].concat('campaignName', 'adSetName', accessors).map(accessor => headers[accessor]);

        return { data, columns, uniqueCampaigns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextAdSets = nextProps.adSets;
        const nextadSetParams = nextAdSets.params;
        const nextTitle = nextProps.title;
        const nextExportName = nextProps.exportName;
        let nextState = null;

        if (nextadSetParams !== prevState.adSetParams) {
            const {
                data,
                columns,
                uniqueCampaigns
            } = SocialAdSetOverviewPivotTable.createTableData(nextAdSets, nextProps);

            nextState = {
                ...prevState,
                columns,
                data,
                uniqueCampaigns,
                title: nextTitle,
                exportName: nextExportName,
                adSetParams: nextadSetParams,
            };
        }
        return nextState;
    }

    render() {
        const { data, title, exportName, columns, uniqueCampaigns } = this.state;

        const expanded = uniqueCampaigns > 1 ? undefined : { 0: true };

        return (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>{title}</ContentHeader>
                <ExportableTable
                    key={1}
                    title={title}
                    exportName={exportName}
                    data={data}
                    columns={columns}
                    expanded={expanded}
                    getTfootProps= {() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    getTheadThProps={this.thProps}
                    showPagination={uniqueCampaigns > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    pivotBy={['campaignName']}
                    defaultSorted={[
                        { id: 'campaignName', desc: false },
                        { id: 'name', desc: false }
                    ]}
                    defaultPageSize={RowsPerPageDefault}
                    minRows={uniqueCampaigns < RowsPerPageDefault ? uniqueCampaigns : RowsPerPageDefault}
                    exportRows={data.length}
                    className="-highlight"
                />
            </ContentTile>
        );
    }
}

SocialAdSetOverviewPivotTable.propTypes = {
    title: PropTypes.string.isRequired,
    exportName: PropTypes.string.isRequired,
    showSemCpconv: PropTypes.bool,
    showConversionRevenue: PropTypes.bool,
    adSets: PropTypes.shape({
        hasData: PropTypes.bool,
        adSetIdMap: PropTypes.object.isRequired,
        params: PropTypes.object
    }).isRequired
};

export default SocialAdSetOverviewPivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/SocialAdSetOverviewPivotTable.js